import { Component, Inject, OnInit } from '@angular/core';
import { IAccount, IVisibleUsers } from 'app/store/models/account.model';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { TABLE_COMPONENT, TableComponent } from '../../../shared/ui/table/table.component';
import { FranchiseeService } from 'app/franchisee/franchisee.service';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { AccountsUnderComponent } from '../accounts-under/accounts-under.component';

@Component({
  selector: 'app-franchisee-control',
  templateUrl: './franchisee-control.component.html',
  styleUrls: ['./franchisee-control.component.scss'],
})
export class FranchiseeControlComponent implements OnInit {
  public column: IAccount;
  public franchisees: IAccount[];
  viewRepButtonProperties: Button = {
    text: 'View reps',
    color: ButtonColor.ORANGE,
    size: ButtonSize.NORMAL,
    style: 'rounded',
    onClick: () => this.openDialog(),
  };

  constructor(
    private store: Store<IReducers>,
    private franchiseeService: FranchiseeService,
    @Inject(TABLE_COMPONENT) private tableComponent: TableComponent,
  ) {
  }

  ngOnInit() {
  }

  getFranchisees() {
    return this.franchisees.map(franchisee => {
      return {
        label: franchisee.email,
        value: franchisee.account_id,
      };
    });
  }

  onFranchiseeSelect(franchiseeId) {
    if (!franchiseeId) {
      return;
    }

    const selectedFranchiseeId = Number(franchiseeId);
    const newFranchisee = this.findFranchisee(selectedFranchiseeId);

    if (!newFranchisee) {
      return;
    }

    if (this.column.franchisee_id && this.column.franchisee_id !== selectedFranchiseeId) {
      const currentFranchisee = this.findFranchisee(this.column.franchisee_id);

      if (currentFranchisee) {
        this.removeUserFromFranchisee(currentFranchisee, accountIds => {
          this.syncFranchiseeVisibleUsers(currentFranchisee, accountIds);
          this.assignUserToFranchisee(newFranchisee);
        });
        return;
      }
    }

    this.assignUserToFranchisee(newFranchisee);
  }

  resetFranchisee() {
    const currentFranchisee = this.findFranchisee(this.column.franchisee_id);

    if (!currentFranchisee) {
      return;
    }

    this.removeUserFromFranchisee(currentFranchisee, accountIds => {
      this.syncFranchiseeVisibleUsers(currentFranchisee, accountIds);
      this.column.franchisee_id = null;
      this.handleSuccess();
    });
  }

  openDialog() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: `User Account(s) under ${this.column.first_name} ${this.column.last_name}`,
          content: AccountsUnderComponent,
          componentData: {
            items: this.column.visible_users,
            account: this.column,
          },
          dialogMinWidth: '1000px',
          canClose: true,
        },
      }),
    );
  }

  private assignUserToFranchisee(franchisee: IAccount) {
    const accountIds = this.getAccountIdsWithCurrentUser(franchisee);

    this.franchiseeService.assignFranchiseeAccount(franchisee.account_id, Number(this.column.account_id)).subscribe(
      () => {
        this.syncFranchiseeVisibleUsers(franchisee, accountIds);
        this.column.franchisee_id = franchisee.account_id;
        this.handleSuccess();
      },
      error => this.handleError(error),
    );
  }

  private removeUserFromFranchisee(franchisee: IAccount, onSuccess: (accountIds: number[]) => void) {
    const accountIds = this.getAccountIdsWithoutCurrentUser(franchisee);

    this.franchiseeService.removeFranchiseeAssignment(franchisee.account_id, Number(this.column.account_id)).subscribe(
      () => onSuccess(accountIds),
      error => this.handleError(error),
    );
  }

  private findFranchisee(franchiseeId: number): IAccount | null {
    return this.franchisees.find(franchisee => franchisee.account_id === Number(franchiseeId)) || null;
  }

  private getAccountIdsWithCurrentUser(franchisee: IAccount): number[] {
    const accountIds = this.getAssignedAccountIds(franchisee);
    const currentAccountId = Number(this.column.account_id);

    if (accountIds.indexOf(currentAccountId) === -1) {
      accountIds.push(currentAccountId);
    }

    return accountIds;
  }

  private getAccountIdsWithoutCurrentUser(franchisee: IAccount): number[] {
    const currentAccountId = Number(this.column.account_id);

    return this.getAssignedAccountIds(franchisee).filter(accountId => accountId !== currentAccountId);
  }

  private getAssignedAccountIds(franchisee: IAccount): number[] {
    return (franchisee.visible_users || [])
      .map((user: IVisibleUsers) => Number(user.id))
      .filter(accountId => !!accountId);
  }

  private syncFranchiseeVisibleUsers(franchisee: IAccount, accountIds: number[]) {
    const visibleUsers = franchisee.visible_users || [];
    const normalizedAccountIds = accountIds.map(accountId => Number(accountId));

    franchisee.visible_users = visibleUsers.filter(user => normalizedAccountIds.indexOf(Number(user.id)) !== -1);

    const currentAccountId = Number(this.column.account_id);

    if (
      normalizedAccountIds.indexOf(currentAccountId) !== -1 &&
      franchisee.visible_users.every(user => Number(user.id) !== currentAccountId)
    ) {
      franchisee.visible_users = [
        ...franchisee.visible_users,
        {
          id: this.column.account_id,
          email: this.column.email,
          first_name: this.column.first_name,
          last_name: this.column.last_name,
          account_role: this.column.account_role,
        },
      ];
    }
  }

  private handleSuccess() {
    this.tableComponent.refresh();
    this.store.dispatch(
      openSnackbar({
        snackbarData: {
          data: {
            message: 'Account settings successfully changed',
            type: 'success',
          },
        },
      }),
    );
  }

  private handleError(error) {
    this.store.dispatch(
      openSnackbar({
        snackbarData: {
          data: {
            message: error.error,
            type: 'error',
          },
        },
      }),
    );
  }
}
